import type { ExistingSkillMatch, SkillSubmission } from '@clockwise/clients/skill-submissions';
import {
  type ListSkillsQuery,
  SKILL_TYPES,
  type Skill,
  type SkillType,
} from '@clockwise/clients/skills';
import { ApiError } from '@clockwise/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  Download,
  Filter,
  Plus,
  Trash2,
  Undo2,
  Upload,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { CatalogFormSheet } from '@/components/custom/catalog-form-sheet';
import {
  PillTabs,
  PillTabsContent,
  PillTabsList,
  PillTabsTrigger,
} from '@/components/custom/pill-tabs';
import { RequirePermission } from '@/components/custom/require-permission';
import { RowActions } from '@/components/custom/row-actions';
import { SearchInput } from '@/components/custom/search-input';
import { SkillImportSheet } from '@/components/custom/skill-import-sheet';
import { StickyTableScroll } from '@/components/custom/sticky-table-scroll';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { skillSubmissionsApi, skillsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const SORT_FIELD_VALUES = ['name', 'createdAt', 'updatedAt'] as const;
type SortField = (typeof SORT_FIELD_VALUES)[number];

const TAB_VALUES = ['master', 'categories', 'other'] as const;
type TabValue = (typeof TAB_VALUES)[number];
const DEFAULT_TAB: TabValue = 'master';

const listSearch = z.object({
  tab: z.enum(TAB_VALUES).optional(),
  q: z.string().optional(),
  status: z.enum(['active', 'inactive', 'all']).optional(),
  type: z.enum(SKILL_TYPES).optional(),
  category: z.string().optional(),
  page: z.coerce.number().int().min(0).optional(),
  sortField: z.enum(SORT_FIELD_VALUES).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
});
type ListSearch = z.infer<typeof listSearch>;

export const Route = createFileRoute('/_authenticated/setup/skills')({
  validateSearch: listSearch,
  component: SkillsPage,
});

const PAGE_SIZE = 25;
const DEFAULT_SORT_FIELD: SortField = 'name';

const TYPE_LABELS: Record<SkillType, string> = { skill: 'Skill', application: 'Application' };

function SkillsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const activeTab: TabValue = search.tab ?? DEFAULT_TAB;
  const sortField: SortField = search.sortField ?? DEFAULT_SORT_FIELD;
  const sortDirection: 'asc' | 'desc' = search.sortDirection ?? 'asc';
  const page = search.page ?? 0;
  const q = search.q ?? '';
  const status = search.status ?? 'active';
  const type = search.type;
  const category = search.category;

  const sorting: SortingState = [{ id: sortField, desc: sortDirection === 'desc' }];

  const updateSearch = (next: Partial<ListSearch>) =>
    navigate({
      search: (prev: ListSearch) => stripDefaults({ ...prev, ...next }),
      replace: true,
    });

  const [editing, setEditing] = useState<Skill | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const queryClient = useQueryClient();

  const exportMutation = useMutation({
    // Mirror the on-screen Master-list filters so the file matches the
    // visible list.
    mutationFn: () =>
      skillsApi.exportXlsx({
        q: q.trim() || undefined,
        active: status === 'all' ? undefined : status === 'active',
        type,
        category,
      }),
    onSuccess: ({ blob, filename }) => {
      triggerDownload(blob, filename ?? 'skills.xlsx');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Export failed');
    },
  });

  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => {
    setSearchInput(q);
  }, [q]);
  useEffect(() => {
    if (searchInput === q) return;
    const handle = setTimeout(() => updateSearch({ q: searchInput, page: 0 }), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, q]);

  const listQuery: ListSkillsQuery = {
    q: q.trim() || undefined,
    active: status === 'all' ? undefined : status === 'active',
    type,
    category,
    sortField,
    sortDirection,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: queryKeys.skills.list(listQuery),
    queryFn: () => skillsApi.list(listQuery),
    placeholderData: keepPreviousData,
  });

  // Category/type rollup — backs the Categories tab and the category filter
  // dropdown. Bounded (tens of rows, one per distinct category) so a single
  // unpaginated fetch is the right call here (domain-builder skill: local
  // rendering is fine when the API returns the whole collection in one call).
  const categoriesQuery = useQuery({
    queryKey: queryKeys.skills.categories(),
    queryFn: () => skillsApi.categories(),
  });
  const categoryRows = categoriesQuery.data?.data ?? [];
  const categoryOptions = useMemo(() => {
    const byType: Record<SkillType, { value: string; label: string }[]> = {
      skill: [],
      application: [],
    };
    for (const row of categoryRows) {
      if (!row.category) continue;
      byType[row.type].push({ value: row.category, label: row.category });
    }
    return byType;
  }, [categoryRows]);

  // "Other" submissions — the "Request a new skill" flow's review queue.
  // Bounded enough (pending requests, not the full history) for one
  // unpaginated fetch, same reasoning as the categories rollup above.
  const submissionsQuery = useQuery({
    queryKey: queryKeys.skillSubmissions.list({ status: 'pending' }),
    queryFn: () => skillSubmissionsApi.list({ status: 'pending' }),
  });
  const submissionRows = submissionsQuery.data?.data ?? [];
  // Requests whose name is already in the catalog (ignoring case) → that skill.
  const existingBySubmission: Record<string, ExistingSkillMatch> =
    submissionsQuery.data?.related?.existingSkills ?? {};

  // Approve dialog: the reviewer picks the Type (and confirms the Category)
  // a new entry is saved with — or, for a request that's already in the
  // catalog, just closes it.
  const [approving, setApproving] = useState<SkillSubmission | null>(null);
  const [approveType, setApproveType] = useState<SkillType>('skill');
  const [approveCategory, setApproveCategory] = useState('');
  const approvingMatch = approving ? existingBySubmission[approving.id] : undefined;
  const openApprove = (sub: SkillSubmission) => {
    setApproving(sub);
    setApproveType('skill');
    setApproveCategory(sub.suggestedCategory ?? '');
  };

  const [rejecting, setRejecting] = useState<SkillSubmission | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const approveMutation = useMutation({
    mutationFn: ({
      id,
      type,
      category,
    }: {
      id: string;
      type?: SkillType;
      category?: string | null;
    }) => skillSubmissionsApi.approve(id, { type, category }),
    onSuccess: (res) => {
      const { outcome, skill } = res.related;
      toast.success(
        outcome === 'linked_existing'
          ? `"${skill.name}" is already in the catalog — request closed.`
          : `"${skill.name}" added to the catalog as ${TYPE_LABELS[skill.type].toLowerCase()}`,
      );
      setApproving(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.skillSubmissions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.skills.all });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Approve failed'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      skillSubmissionsApi.reject(id, { reason }),
    onSuccess: () => {
      toast.success('Submission rejected');
      queryClient.invalidateQueries({ queryKey: queryKeys.skillSubmissions.all });
      setRejecting(null);
      setRejectReason('');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Reject failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => skillsApi.remove(id),
    onSuccess: () => {
      toast.success('Skill deactivated');
      queryClient.invalidateQueries({ queryKey: queryKeys.skills.all });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Delete failed'),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => skillsApi.restore(id),
    onSuccess: () => {
      toast.success('Skill reactivated');
      queryClient.invalidateQueries({ queryKey: queryKeys.skills.all });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Restore failed'),
  });

  const columns = useMemo<ColumnDef<Skill>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <span className="font-medium">
            {row.original.name}
            {!row.original.active ? (
              <span className="ml-2 text-xs text-muted-foreground">(inactive)</span>
            ) : null}
          </span>
        ),
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) => <Badge variant="secondary">{TYPE_LABELS[row.original.type]}</Badge>,
      },
      {
        accessorKey: 'category',
        header: 'Category',
        cell: ({ row }) =>
          row.original.category ?? <span className="text-muted-foreground">—</span>,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.description ?? <span className="text-muted-foreground">—</span>,
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {new Date(row.original.updatedAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions>
            {row.original.active ? (
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Deactivate"
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm(`Deactivate "${row.original.name}"?`)) {
                    deleteMutation.mutate(row.original.id);
                  }
                }}
                disabled={deleteMutation.isPending}
              >
                <Trash2 />
              </Button>
            ) : (
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Reactivate"
                onClick={(e) => {
                  e.stopPropagation();
                  restoreMutation.mutate(row.original.id);
                }}
                disabled={restoreMutation.isPending}
              >
                <Undo2 />
              </Button>
            )}
          </RowActions>
        ),
      },
    ],
    [deleteMutation, restoreMutation],
  );

  const table = useReactTable({
    data: query.data?.data ?? [],
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      if (!first) {
        updateSearch({ sortField: undefined, sortDirection: undefined });
        return;
      }
      updateSearch({
        sortField: first.id as SortField,
        sortDirection: first.desc ? 'desc' : 'asc',
        page: 0,
      });
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.max(1, Math.ceil((query.data?.meta.total ?? 0) / PAGE_SIZE)),
  });

  const total = query.data?.meta.total ?? 0;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, page * PAGE_SIZE + (query.data?.data.length ?? 0));

  const categoryFieldOptions = [...categoryOptions.skill, ...categoryOptions.application];

  return (
    <PageShell>
      <PageHeader
        title="Skills"
        subtitle="Competencies and applications that can be assigned to users (Transfer Pricing, QuickBooks Online, …)."
        count={
          activeTab === 'master'
            ? total
            : activeTab === 'categories'
              ? categoryRows.length
              : undefined
        }
      >
        {activeTab === 'master' && (
          <>
            <RequirePermission permission="skills.view">
              <Button
                variant="outline"
                onClick={() => exportMutation.mutate()}
                disabled={exportMutation.isPending}
              >
                <Download />
                {exportMutation.isPending ? 'Exporting…' : 'Export'}
              </Button>
            </RequirePermission>
            <RequirePermission permission="skills.edit">
              <Button variant="outline" onClick={() => setImportOpen(true)}>
                <Upload />
                Import
              </Button>
            </RequirePermission>
          </>
        )}
        <RequirePermission permission="skills.edit">
          <Button
            onClick={() => {
              setEditing(null);
              setSheetOpen(true);
            }}
          >
            <Plus />
            Add skill
          </Button>
        </RequirePermission>
      </PageHeader>

      <PillTabs
        value={activeTab}
        onValueChange={(v) =>
          updateSearch({ tab: v === DEFAULT_TAB ? undefined : (v as TabValue) })
        }
      >
        <PillTabsList fullWidth>
          <PillTabsTrigger value="master">Master list</PillTabsTrigger>
          <PillTabsTrigger value="categories">Categories</PillTabsTrigger>
          <PillTabsTrigger value="other">
            "Other" submissions
            {submissionRows.length > 0 ? (
              <Badge variant="secondary" className="ml-1.5">
                {submissionRows.length}
              </Badge>
            ) : null}
          </PillTabsTrigger>
        </PillTabsList>

        <PillTabsContent value="master" className="space-y-3">
          <div
            data-surface="panel"
            className="flex flex-wrap items-center gap-3 rounded-md border bg-card p-6 py-4"
          >
            <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
              <Filter className="size-3.5" />
              Filters:
            </span>
            <SearchInput
              placeholder="Search name, description…"
              value={searchInput}
              onValueChange={setSearchInput}
            />
            <Select
              value={status}
              onValueChange={(v) => updateSearch({ status: v as ListSearch['status'], page: 0 })}
            >
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={type ?? 'all'}
              onValueChange={(v) =>
                updateSearch({ type: v === 'all' ? undefined : (v as SkillType), page: 0 })
              }
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="skill">Skill</SelectItem>
                <SelectItem value="application">Application</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={category ?? 'all'}
              onValueChange={(v) =>
                updateSearch({ category: v === 'all' ? undefined : v, page: 0 })
              }
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categoryOptions.skill.length > 0 ? (
                  <SelectGroup>
                    <SelectLabel>Skill categories</SelectLabel>
                    {categoryOptions.skill.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ) : null}
                {categoryOptions.application.length > 0 ? (
                  <SelectGroup>
                    <SelectLabel>Application categories</SelectLabel>
                    {categoryOptions.application.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ) : null}
              </SelectContent>
            </Select>
          </div>

          <StickyTableScroll>
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((hg) => (
                  <TableRow key={hg.id}>
                    {hg.headers.map((h) => {
                      const sortable = h.column.getCanSort();
                      const dir = h.column.getIsSorted();
                      return (
                        <TableHead key={h.id}>
                          {sortable ? (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 select-none hover:text-foreground"
                              onClick={h.column.getToggleSortingHandler()}
                            >
                              {flexRender(h.column.columnDef.header, h.getContext())}
                              {dir === 'asc' ? (
                                <ArrowUp className="size-3" />
                              ) : dir === 'desc' ? (
                                <ArrowDown className="size-3" />
                              ) : (
                                <ArrowUpDown className="size-3 opacity-40" />
                              )}
                            </button>
                          ) : (
                            flexRender(h.column.columnDef.header, h.getContext())
                          )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {query.isLoading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <TableRow key={`skeleton-${i}`}>
                      {columns.map((_c, j) => (
                        <TableCell key={`skeleton-cell-${i}-${j}`}>
                          <Skeleton className="h-4 w-20" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : query.isError ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="py-10 text-center text-destructive"
                    >
                      {query.error instanceof ApiError
                        ? query.error.message
                        : 'Failed to load skills.'}
                    </TableCell>
                  </TableRow>
                ) : table.getRowModel().rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="py-10 text-center text-muted-foreground"
                    >
                      {q || type || category
                        ? 'No skills match these filters.'
                        : 'No skills yet. Click “Add skill”.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((r) => (
                    <TableRow
                      key={r.id}
                      className="cursor-pointer"
                      onClick={() => {
                        setEditing(r.original);
                        setSheetOpen(true);
                      }}
                    >
                      {r.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell ?? defaultCell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </StickyTableScroll>

          <footer className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {total === 0 ? 'No results' : `Showing ${showingFrom}–${showingTo} of ${total}`}
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => updateSearch({ page: Math.max(0, page - 1) })}
                disabled={page === 0 || query.isFetching}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => updateSearch({ page: page + 1 })}
                disabled={(page + 1) * PAGE_SIZE >= total || query.isFetching}
              >
                Next
              </Button>
            </div>
          </footer>
        </PillTabsContent>

        <PillTabsContent value="categories" className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Active skills grouped by category. Rename/merge isn't supported yet — this is a
            read-only summary for now.
          </p>
          <StickyTableScroll>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Entries</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categoriesQuery.isLoading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <TableRow key={`cat-skeleton-${i}`}>
                      <TableCell colSpan={3}>
                        <Skeleton className="h-4 w-40" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : categoriesQuery.isError ? (
                  <TableRow>
                    <TableCell colSpan={3} className="py-10 text-center text-destructive">
                      {categoriesQuery.error instanceof ApiError
                        ? categoriesQuery.error.message
                        : 'Failed to load categories.'}
                    </TableCell>
                  </TableRow>
                ) : categoryRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="py-10 text-center text-muted-foreground">
                      No active skills yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  categoryRows
                    .slice()
                    .sort((a, b) => (a.category ?? '').localeCompare(b.category ?? ''))
                    .map((row, i) => (
                      <TableRow key={`${row.category ?? 'uncategorized'}-${row.type}-${i}`}>
                        <TableCell className="font-medium">
                          {row.category ?? (
                            <span className="text-muted-foreground">Uncategorized</span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {TYPE_LABELS[row.type]}
                        </TableCell>
                        <TableCell>{row.count}</TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </StickyTableScroll>
          <footer className="text-xs text-muted-foreground">
            {categoryRows.length === 0 ? null : `Showing all ${categoryRows.length} categories`}
          </footer>
        </PillTabsContent>

        <PillTabsContent value="other" className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Skills/applications employees proposed from "My Skills". Approve lets you choose the
            type (Skill or Application) and category the new entry is saved with. A request for
            something already in the master list is marked "In catalog" — approving it just closes
            the request; nothing new is added.
          </p>
          <StickyTableScroll>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Proposed name</TableHead>
                  <TableHead>Suggested category</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissionsQuery.isLoading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <TableRow key={`sub-skeleton-${i}`}>
                      <TableCell colSpan={5}>
                        <Skeleton className="h-4 w-48" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : submissionsQuery.isError ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center text-destructive">
                      {submissionsQuery.error instanceof ApiError
                        ? submissionsQuery.error.message
                        : 'Failed to load submissions.'}
                    </TableCell>
                  </TableRow>
                ) : submissionRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                      No pending submissions.
                    </TableCell>
                  </TableRow>
                ) : (
                  submissionRows.map((sub) => (
                    <TableRow key={sub.id}>
                      <TableCell className="font-medium">
                        <span className="inline-flex items-center gap-2">
                          {sub.proposedName}
                          {existingBySubmission[sub.id] ? (
                            <Badge
                              variant="outline"
                              title={`Already in the catalog as "${existingBySubmission[sub.id]?.name}"`}
                            >
                              In catalog
                            </Badge>
                          ) : null}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {sub.suggestedCategory ?? <span>—</span>}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(sub.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="max-w-[260px] text-xs text-muted-foreground">
                        {sub.note ?? '—'}
                      </TableCell>
                      <TableCell>
                        <RequirePermission permission="skills.edit">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="icon-sm"
                              variant="ghost"
                              aria-label="Approve"
                              onClick={() => openApprove(sub)}
                              disabled={approveMutation.isPending}
                            >
                              <Check />
                            </Button>
                            <Button
                              size="icon-sm"
                              variant="ghost"
                              aria-label="Reject"
                              onClick={() => setRejecting(sub)}
                            >
                              <X />
                            </Button>
                          </div>
                        </RequirePermission>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </StickyTableScroll>
          <footer className="text-xs text-muted-foreground">
            {submissionRows.length === 0
              ? null
              : `Showing ${submissionRows.length} pending submission${submissionRows.length === 1 ? '' : 's'}`}
          </footer>
        </PillTabsContent>
      </PillTabs>

      <CatalogFormSheet
        open={sheetOpen}
        onOpenChange={(o) => {
          setSheetOpen(o);
          if (!o) setEditing(null);
        }}
        entityLabel="skill"
        entity={
          editing
            ? {
                id: editing.id,
                name: editing.name,
                description: editing.description,
                category: editing.category,
                kind: editing.type === 'application',
              }
            : null
        }
        kindField={{
          label: 'Type',
          trueLabel: 'Application',
          falseLabel: 'Skill',
          hint: 'Applications show a slightly different proficiency scale to employees (e.g. "Basic User" instead of "Basic").',
        }}
        categoryField={{
          label: 'Category',
          options: categoryFieldOptions,
          placeholder: 'Uncategorized',
          hint: 'Pick an existing category, or save with one typed on My Skills via "Request a new skill" — admins can relabel it later.',
        }}
        submit={(body) => {
          const payload = {
            name: body.name,
            description: body.description,
            category: body.category ? body.category : null,
            type:
              body.kind === undefined
                ? undefined
                : body.kind
                  ? ('application' as const)
                  : ('skill' as const),
          };
          return editing ? skillsApi.patch(editing.id, payload) : skillsApi.create(payload);
        }}
        invalidateKeys={[queryKeys.skills.all, queryKeys.audit.all]}
      />

      <SkillImportSheet open={importOpen} onOpenChange={setImportOpen} />

      <Dialog
        open={approving !== null}
        onOpenChange={(open) => {
          if (!open) setApproving(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {approvingMatch ? 'Already in the catalog' : `Approve "${approving?.proposedName}"`}
            </DialogTitle>
            <DialogDescription>
              {approvingMatch
                ? `"${approving?.proposedName}" is already in the catalog as "${approvingMatch.name}" (${TYPE_LABELS[approvingMatch.type]}${approvingMatch.active ? '' : ', deactivated'}). Closing the request links it to that entry — nothing new is added.`
                : 'Adds it to the master list so employees can rate it.'}
            </DialogDescription>
          </DialogHeader>
          {approvingMatch ? null : (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="approve-type">Type</Label>
                <Select value={approveType} onValueChange={(v) => setApproveType(v as SkillType)}>
                  <SelectTrigger id="approve-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SKILL_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {TYPE_LABELS[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Applications show employees a slightly different proficiency scale (e.g. "Basic
                  User" instead of "Basic").
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="approve-category">Category</Label>
                <Input
                  id="approve-category"
                  list="approve-category-options"
                  value={approveCategory}
                  onChange={(e) => setApproveCategory(e.target.value)}
                  placeholder="Uncategorized"
                />
                <datalist id="approve-category-options">
                  {/* Suggestions follow the chosen type — same split as the
                      master list's category filter. Free text still allowed. */}
                  {categoryOptions[approveType].map((c) => (
                    <option key={c.value} value={c.value} />
                  ))}
                </datalist>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproving(null)}>
              Cancel
            </Button>
            <Button
              disabled={approveMutation.isPending}
              onClick={() => {
                if (!approving) return;
                approveMutation.mutate(
                  approvingMatch
                    ? { id: approving.id }
                    : {
                        id: approving.id,
                        type: approveType,
                        category: approveCategory.trim() || null,
                      },
                );
              }}
            >
              {approvingMatch ? 'Close request' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={rejecting !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRejecting(null);
            setRejectReason('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reject "{rejecting?.proposedName}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="reject-reason">Reason</Label>
            <Textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Why this request isn't being added to the catalog"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={rejectReason.trim().length === 0 || rejectMutation.isPending}
              onClick={() => {
                if (!rejecting) return;
                rejectMutation.mutate({ id: rejecting.id, reason: rejectReason.trim() });
              }}
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}

const defaultCell: ColumnDef<Skill>['cell'] = ({ getValue }) => String(getValue() ?? '');

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function stripDefaults(s: ListSearch): ListSearch {
  const out: ListSearch = {};
  if (s.tab && s.tab !== DEFAULT_TAB) out.tab = s.tab;
  if (s.q && s.q.length > 0) out.q = s.q;
  if (s.status && s.status !== 'active') out.status = s.status;
  if (s.type) out.type = s.type;
  if (s.category) out.category = s.category;
  if (s.page && s.page > 0) out.page = s.page;
  if (s.sortField && s.sortField !== DEFAULT_SORT_FIELD) out.sortField = s.sortField;
  if (s.sortDirection && s.sortDirection !== 'asc') out.sortDirection = s.sortDirection;
  return out;
}
